import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { FiDownload, FiShare2, FiX } from 'react-icons/fi';
import './StudentQRCode.css';

// Helper function to get the correct image URL (handles both local and Supabase URLs)
const getImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  // If URL already starts with http/https, it's a full URL (Supabase)
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }
  // Otherwise, it's a local path that needs the server prefix
  return `http://localhost:5000${imageUrl}`;
};

const StudentQRCode = ({ student, onClose }) => {
  if (!student) return null;

  // Create a simple info string for QR code
  const qrValue = `CCS-SIS Student Profile
ID: ${student.studentId}
Name: ${student.firstName} ${student.lastName}
Program: ${student.program || 'BSCS'}
Year: ${student.yearLevel} - Section ${student.section}
Email: ${student.email || 'N/A'}
Status: ${student.enrollmentStatus}`;

  const downloadQRCode = () => {
    const svg = document.getElementById('student-qr-code');
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      
      const pngFile = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.download = `QR_${student.studentId}_${student.lastName}.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const shareQRCode = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `QR Code - ${student.firstName} ${student.lastName}`,
          text: qrValue,
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(qrValue);
      alert('Student info copied to clipboard!');
    }
  };

  return (
    <div className="qr-modal-overlay" onClick={onClose}>
      <div className="qr-modal" onClick={(e) => e.stopPropagation()}>
        <div className="qr-modal-header">
          <h3>Student QR Code</h3>
          <button className="close-btn" onClick={onClose}>
            <FiX />
          </button>
        </div>
        
        <div className="qr-modal-content">
          <div className="qr-code-wrapper">
            <QRCodeSVG
              id="student-qr-code"
              value={qrValue}
              size={200}
              level="H"
              includeMargin={true}
              bgColor="#ffffff"
              fgColor="#3D0C0C"
            />
          </div>
          
          <div className="student-info-preview">
            <div className="student-avatar-qr">
              {student.profileImage ? (
                <img 
                  src={getImageUrl(student.profileImage)} 
                  alt={`${student.firstName} ${student.lastName}`}
                  className="student-profile-img"
                />
              ) : (
                <div className="student-initials">
                  {student.firstName?.charAt(0)}{student.lastName?.charAt(0)}
                </div>
              )}
            </div>
            <h4>{student.firstName} {student.lastName}</h4>
            <span className="student-id-badge">{student.studentId}</span>
            <p className="student-program">{student.program || 'BS Computer Science'}</p>
            <div className="student-tags">
              <span className="tag">Year {student.yearLevel}</span>
              <span className="tag">Section {student.section}</span>
              <span className={`tag status ${student.enrollmentStatus?.toLowerCase().replace(' ', '-')}`}>
                {student.enrollmentStatus}
              </span>
            </div>
          </div>
        </div>
        
        <div className="qr-modal-actions">
          <button className="btn-download" onClick={downloadQRCode}>
            <FiDownload /> Download QR
          </button>
          <button className="btn-share" onClick={shareQRCode}>
            <FiShare2 /> Share
          </button>
        </div>
        
        <p className="qr-note">
          Scan this QR code to view student information
        </p>
      </div>
    </div>
  );
};

export default StudentQRCode;
